import { Component, inject, signal } from '@angular/core';
import { describeFilingUpload, buildFilingUpload } from '../../engine/filing-upload';
import { FileUploadZoneComponent } from '../../shared/components/file-upload-zone/file-upload-zone.component';
import { JobStatusLineComponent } from '../../shared/components/job-status-line/job-status-line.component';
import { PyodideArelleEngine } from '../../engine/pyodide-arelle.engine';
import type { ValidationMessage } from '../../engine/xbrl-engine.types';

@Component({
  selector: 'app-validate-filing',
  imports: [FileUploadZoneComponent, JobStatusLineComponent],
  templateUrl: './validate-filing.component.html',
  styleUrl: './validate-filing.component.scss',
})
export class ValidateFilingComponent {
  private readonly engine = inject(PyodideArelleEngine);

  protected readonly phase = signal<string | null>(null);
  protected readonly busy = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly filename = signal<string | null>(null);
  protected readonly counts = signal<{ error: number; warning: number; info: number } | null>(
    null,
  );
  protected readonly messages = signal<ValidationMessage[]>([]);
  protected readonly totalMs = signal<number | null>(null);

  protected onFiles(files: File[]): void {
    if (!files.length || this.busy()) return;
    void this.runValidate(files);
  }

  private async runValidate(files: File[]): Promise<void> {
    this.busy.set(true);
    this.error.set(null);
    this.phase.set('starting');
    this.counts.set(null);
    this.messages.set([]);
    this.totalMs.set(null);

    try {
      const payload = await buildFilingUpload(files);
      this.filename.set(describeFilingUpload(payload));

      this.engine.validate(files).subscribe({
        next: (event) => {
          if (event.kind === 'progress') {
            this.phase.set(event.data.phase);
          } else {
            this.counts.set({
              error: event.data.counts.error,
              warning: event.data.counts.warning,
              info: event.data.counts.info,
            });
            this.messages.set(event.data.messages);
            this.totalMs.set(event.data.totalMs);
            this.phase.set('ready');
            this.busy.set(false);
          }
        },
        error: (err: unknown) => {
          this.error.set(err instanceof Error ? err.message : String(err));
          this.busy.set(false);
          this.phase.set(null);
        },
      });
    } catch (err: unknown) {
      this.error.set(err instanceof Error ? err.message : String(err));
      this.busy.set(false);
      this.phase.set(null);
    }
  }
}
